import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Home, Store } from "lucide-react";
import Reveal, { Overline } from "@/components/Reveal";
import { MODELS, TIME_SLOTS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { model: "", driveType: "showroom", date: "", slot: "", name: "", phone: "", email: "" };

export default function TestDrive({ selectedModel }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const model = form.model || selectedModel || "";
  const today = new Date().toISOString().split("T")[0];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext =
    step === 0 ? !!model :
    step === 1 ? !!form.date && !!form.slot :
    form.name.trim().length >= 2 && /^[0-9+\s-]{10,15}$/.test(form.phone.trim());

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/test-drive`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        model,
        drive_type: form.driveType,
        preferred_date: form.date,
        time_slot: form.slot,
      });
      setDone(data);
      toast.success("Test drive booked! Our team will call you to confirm.");
    } catch (e) {
      toast.error("Could not submit. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setStep(0);
    setDone(null);
  };

  const modelName = MODELS.find((m) => m.id === model)?.name || model;

  return (
    <section id="test-drive" data-testid="test-drive-section" className="relative bg-[#0A0F14] py-24 sm:py-32 overflow-hidden">
      <div className="absolute -left-40 top-1/3 w-[500px] h-[500px] rounded-full bg-[#FFFFFF]/8 blur-[160px] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <Overline className="justify-center flex">Reserve Your Seat</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-5xl text-white">
            Book a <span className="text-[#FFFFFF]">Test Drive.</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-lg mx-auto">
            At our NH-20 Panikoili flagship — or we bring the car to your doorstep.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 bg-[#0D1822] border border-white/10 clip-card p-6 sm:p-10">
            {done ? (
              <motion.div
                data-testid="test-drive-confirmation"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
              >
                <CheckCircle2 size={56} className="mx-auto text-[#FFFFFF]" />
                <h3 className="mt-6 font-display font-extrabold text-2xl sm:text-3xl text-white">You're Booked.</h3>
                <p className="mt-4 text-slate-400 max-w-md mx-auto leading-relaxed">
                  {done.name}, your {modelName} {done.drive_type === "home" ? "doorstep" : "showroom"} test drive
                  is requested for <span className="text-white">{done.preferred_date}</span> at{" "}
                  <span className="text-white">{done.time_slot}</span>. Reference:{" "}
                  <span className="font-mono text-[#FFFFFF]">{done.id.slice(0, 8).toUpperCase()}</span>
                </p>
                <button
                  data-testid="test-drive-book-another-button"
                  onClick={reset}
                  className="mt-8 border border-white/20 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.18em] px-8 py-3.5 transition-colors duration-300"
                >
                  Book Another
                </button>
              </motion.div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-10" data-testid="test-drive-progress">
                  {["Model", "Schedule", "Details"].map((label, i) => (
                    <div key={label} className="flex items-center gap-3 flex-1">
                      <span
                        data-testid={`test-drive-step-indicator-${i}`}
                        className={`w-8 h-8 grid place-items-center font-mono text-xs border transition-colors duration-300 ${
                          step >= i ? "bg-[#FFFFFF] border-[#FFFFFF] text-black" : "border-white/15 text-slate-500"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className={`hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] ${step >= i ? "text-white" : "text-slate-600"}`}>
                        {label}
                      </span>
                      {i < 2 && <span className={`flex-1 h-px ${step > i ? "bg-[#FFFFFF]" : "bg-white/10"}`} />}
                    </div>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div key="s0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
                      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-5">Choose your Kia</p>
                      <div data-testid="test-drive-model-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {MODELS.map((m) => (
                          <button
                            key={m.id}
                            data-testid={`test-drive-model-option-${m.id}`}
                            onClick={() => set("model", m.id)}
                            className={`text-left p-4 border transition-colors duration-300 ${
                              model === m.id ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-white/10 hover:border-white/30"
                            }`}
                          >
                            <p className="font-display font-bold text-white text-sm">{m.name.replace("Kia ", "")}</p>
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 mt-1">{m.category}</p>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
                      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-5">Where should we meet?</p>
                      <div className="grid sm:grid-cols-2 gap-3 mb-8">
                        {[
                          { id: "showroom", label: "At the Showroom", sub: "NH-20, Panikoili Flagship", Icon: Store },
                          { id: "home", label: "At My Doorstep", sub: "Across Jajpur & Nearby", Icon: Home },
                        ].map(({ id, label, sub, Icon }) => (
                          <button
                            key={id}
                            data-testid={`test-drive-type-${id}`}
                            onClick={() => set("driveType", id)}
                            className={`flex items-center gap-4 p-5 border text-left transition-colors duration-300 ${
                              form.driveType === id ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-white/10 hover:border-white/30"
                            }`}
                          >
                            <Icon size={22} className={form.driveType === id ? "text-[#FFFFFF]" : "text-slate-500"} />
                            <span>
                              <span className="block font-display font-bold text-white text-sm">{label}</span>
                              <span className="block text-xs text-slate-500 mt-0.5">{sub}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Preferred Date</label>
                          <input
                            data-testid="test-drive-date-input"
                            type="date" min={today} value={form.date}
                            onChange={(e) => set("date", e.target.value)}
                            className="kia-input w-full"
                          />
                        </div>
                        <div>
                          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Time Slot</label>
                          <div className="flex flex-wrap gap-2">
                            {TIME_SLOTS.map((s) => (
                              <button
                                key={s}
                                data-testid={`test-drive-slot-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                onClick={() => set("slot", s)}
                                className={`font-mono text-xs px-3.5 py-2.5 border transition-colors duration-300 ${
                                  form.slot === s ? "bg-[#FFFFFF] border-[#FFFFFF] text-black" : "border-white/15 text-slate-400 hover:border-white/40"
                                }`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
                      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-5">Your details</p>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Full Name *</label>
                          <input
                            data-testid="test-drive-name-input"
                            type="text" placeholder="e.g. Sanjib Banerjee" value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            className="kia-input w-full"
                          />
                        </div>
                        <div>
                          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Mobile Number *</label>
                          <input
                            data-testid="test-drive-phone-input"
                            type="tel" placeholder="+91 98XXX XXXXX" value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            className="kia-input w-full"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Email (optional)</label>
                          <input
                            data-testid="test-drive-email-input"
                            type="email" placeholder="you@example.com" value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            className="kia-input w-full"
                          />
                        </div>
                      </div>
                      <p className="mt-5 text-xs text-slate-600">
                        Booking: <span className="text-slate-300">{modelName}</span> ·{" "}
                        <span className="text-slate-300">{form.driveType === "home" ? "Doorstep" : "Showroom"}</span> ·{" "}
                        <span className="text-slate-300">{form.date}</span> · <span className="text-slate-300">{form.slot}</span>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-7">
                  <button
                    data-testid="test-drive-back-button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  {step < 2 ? (
                    <button
                      data-testid="test-drive-next-button"
                      onClick={() => canNext && setStep((s) => s + 1)}
                      disabled={!canNext}
                      className="inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] disabled:opacity-40 disabled:pointer-events-none text-black text-xs font-semibold uppercase tracking-[0.18em] px-8 py-3.5 clip-notch transition-colors duration-300"
                    >
                      Continue <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button
                      data-testid="test-drive-submit-button"
                      onClick={submit}
                      disabled={!canNext || submitting}
                      className="inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] disabled:opacity-40 disabled:pointer-events-none text-black text-xs font-semibold uppercase tracking-[0.18em] px-8 py-3.5 clip-notch transition-colors duration-300"
                    >
                      {submitting ? "Booking..." : "Confirm Booking"} <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
